// Token Management Service
// Handles storage, retrieval, validation, and refresh of authentication tokens

const TOKEN_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  TOKEN_TYPE: 'token_type',
  EXPIRES_AT: 'expires_at',
};

class TokenService {
  // Token Storage Methods
  setTokens(accessToken, refreshToken, tokenType = 'bearer', expiresAt = null) {
    if (accessToken) {
      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
    }
    if (tokenType) {
      localStorage.setItem(TOKEN_KEYS.TOKEN_TYPE, tokenType);
    }
    if (expiresAt) {
      localStorage.setItem(TOKEN_KEYS.EXPIRES_AT, expiresAt);
    }
  }

  getAccessToken() {
    return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
  }

  getRefreshToken() {
    return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
  }

  getTokenType() {
    return localStorage.getItem(TOKEN_KEYS.TOKEN_TYPE) || 'bearer';
  }

  getExpiresAt() {
    return localStorage.getItem(TOKEN_KEYS.EXPIRES_AT);
  }

  clearTokens() {
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.TOKEN_TYPE);
    localStorage.removeItem(TOKEN_KEYS.EXPIRES_AT);
  }

  // Token Validation Methods
  isTokenExpired() {
    const expiresAt = this.getExpiresAt();
    if (!expiresAt) {
      // If no expiry info, assume token is valid (backward compatibility)
      return false;
    }
    
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    return now >= expiryDate;
  }

  isTokenExpiringSoon(bufferMinutes = 5) {
    const expiresAt = this.getExpiresAt();
    if (!expiresAt) {
      return false;
    }
    
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const bufferMs = bufferMinutes * 60 * 1000;
    
    return (expiryDate.getTime() - now.getTime()) <= bufferMs;
  }

  getTimeUntilExpiry() {
    const expiresAt = this.getExpiresAt();
    if (!expiresAt) {
      return null;
    }
    
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const msUntilExpiry = expiryDate.getTime() - now.getTime();
    
    if (msUntilExpiry <= 0) {
      return 0;
    }
    
    return msUntilExpiry;
  }

  // Authorization Header Helper
  getAuthHeader() {
    const token = this.getAccessToken();
    const tokenType = this.getTokenType();
    
    if (!token) {
      return null;
    }
    
    return `${tokenType.charAt(0).toUpperCase() + tokenType.slice(1)} ${token}`;
  }

  // Session Storage (for temporary data)
  setSessionData(key, value) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  getSessionData(key) {
    const data = sessionStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  }

  clearSessionData(key) {
    sessionStorage.removeItem(key);
  }

  // Full cleanup
  clearAll() {
    this.clearTokens();
    sessionStorage.clear();
  }
}

// Export singleton instance
export const tokenService = new TokenService();

// Export class for testing purposes
export default TokenService;